import { useEffect } from "react";
import { getSocket } from "@/lib/socketClient";
import { onResync } from "@/lib/socketRecovery";
import { mocksEnabled } from "@/portals/physician/api/transport";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";
import { useNotificationsStore } from "@/stores/notificationsStore";
import { useReviewItemsCountStore } from "@/stores/reviewItemsCountStore";
import type { NotificationType } from "@/stores/notificationsStore";

// Any of these can change how many items await the patient.
const COUNT_CHANGING: ReadonlySet<NotificationType> = new Set(["REVIEW_ITEM_NEEDS_INPUT", "REVIEW_ITEM_QUESTION", "REVIEW_ITEM_RESOLVED"]);

/**
 * Keeps the review badge AND the review data current: on load, whenever the
 * viewed profile changes, and whenever a review notification (new item, a
 * physician's question, a decision) lands in the notifications store —
 * whether over the socket or the list's polling fallback. The records refresh
 * is what updates the Human Review Queue (incl. the Patient Conversation) and
 * the Medications tab at once; before, only the badge changed and those
 * screens waited for the 60 s fallback or a page refresh.
 */
export function useReviewItemsCountSync(): void {
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const refresh = useReviewItemsCountStore((s) => s.refresh);
  const { refresh: refreshRecords } = useParsedRecords();

  useEffect(() => {
    void refresh(actingAsProfileId);
  }, [refresh, actingAsProfileId]);

  useEffect(() => {
    const seen = new Set(useNotificationsStore.getState().notifications.map((n) => n.id));
    return useNotificationsStore.subscribe((state) => {
      let sawNewReviewItem = false;
      for (const n of state.notifications) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        if (COUNT_CHANGING.has(n.type)) sawNewReviewItem = true;
      }
      if (sawNewReviewItem) {
        void refresh(actingAsProfileId);
        refreshRecords();
      }
    });
  }, [refresh, refreshRecords, actingAsProfileId]);

  // After a reconnect / server resync, events may have been missed: refetch.
  useEffect(() => {
    if (mocksEnabled()) return;
    return onResync(getSocket(), () => {
      void refresh(actingAsProfileId);
      refreshRecords();
    });
  }, [refresh, refreshRecords, actingAsProfileId]);
}
